// account_view — PURE. How the Account page reads a plan against what is left.
import type { AccountView, Plan, TopUp, FreeView } from "../api";

/** `$12.40`, `$0.05`, `$1,250.00` — cents as the console's English writes a price; a negative
 *  balance (the last run finished past the line) reads as `$0.00` left. */
export function formatUsdCents(cents: number): string {
  if (!Number.isFinite(cents)) return "—";
  const c = Math.max(0, Math.round(cents));
  return `$${(c / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** `1.3 GB`, `5 GB`, `250 MB`, `12 KB` — bytes at the width a human reads. */
export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return "—";
  if (n < 1024)        return `${n} B`;
  if (n < 1024 ** 2)   return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 ** 3)   return `${sansZero((n / 1024 ** 2).toFixed(n < 10 * 1024 ** 2 ? 1 : 0))} MB`;
  return `${sansZero((n / 1024 ** 3).toFixed(1))} GB`;
}

/** `5.0` → `5`. Une décimale qui ne dit rien est du bruit, et une carte de prix en aligne trois. */
function sansZero(s: string): string {
  return s.endsWith(".0") ? s.slice(0, -2) : s;
}

export type Gauge = { used: number; allowed: number; pct: number; tone: "ok" | "warn" | "over"; label: string };

/** A used/allowed pair as a gauge: over 80 % warns, at 100 % is over; no allowance is always "over" (nothing to spend). */
export function gauge(used: number, allowed: number, fmt: (n: number) => string = String): Gauge {
  const pct = allowed > 0 ? Math.min(100, Math.round((used / allowed) * 100)) : (used > 0 ? 100 : 0);
  const tone = allowed <= 0 || used >= allowed ? "over" : pct >= 80 ? "warn" : "ok";
  return { used, allowed, pct, tone, label: `${fmt(used)} / ${fmt(allowed)}` };
}

/** The credit as a gauge of what is LEFT over what the plan credits each month, both in cents: full = a whole month ahead. */
export function balanceGauge(balanceCents: number, perMonthCents: number): Gauge {
  const left = Math.max(0, balanceCents);
  const pct = perMonthCents > 0 ? Math.min(100, Math.round((left / perMonthCents) * 100)) : 0;
  const tone = perMonthCents <= 0 || left <= 0 ? "over" : pct <= 20 ? "warn" : "ok";
  return { used: left, allowed: perMonthCents, pct, tone, label: `${formatUsdCents(left)} left` };
}

export const PLAN_NAME: Record<Plan["id"], string> = { S: "Solo", M: "Duo", L: "Studio", H: "Hosting" };

/** `€5 + VAT / month · or €50 / year`; a legacy plan keeps `$15.99 / month`. */
export function priceLine(p: Pick<Plan, "price_usd" | "price_eur" | "price_eur_year" | "vat">): string {
  if (p.price_eur == null) return `$${p.price_usd} / month`;
  const vat = p.vat === "excluded" ? " + VAT" : "";
  return `€${p.price_eur}${vat} / month${p.price_eur_year != null ? ` · or €${p.price_eur_year} / year` : ""}`;
}

/** `€25 · $30.42 credit`. */
export function topupLabel(t: TopUp): string {
  return `€${t.price_eur} · ${formatUsdCents(t.credit_cents)} credit`;
}

/** The cards to show: what is on sale, plus the legacy plan this account holds — never another legacy one. */
export function cardsShown(v: Pick<AccountView, "plans" | "sold" | "plan">): Plan[] {
  return v.plans.filter((p) => v.sold.includes(p.id) || v.plan?.id === p.id);
}

/**
 * The build-credit gauge. A plan with a month of credit: what is left over that month. The
 * hosting plan has none — it runs on top-ups — so the gauge only says what is left. The free
 * trial: what is left of the gift.
 */
export function creditGauge(v: Pick<AccountView, "plan" | "credit" | "free">): Gauge {
  const perMonth = Math.round((v.plan?.credit_usd_per_month ?? 0) * 100);
  if (perMonth > 0) return balanceGauge(v.credit.balance_cents, perMonth);
  // Not started: the gift is not credited yet — say what is waiting, not "$0.00 left".
  if (!v.plan && v.free?.state === "open" && v.credit.balance_cents === 0) return { used: v.free.gift_cents, allowed: v.free.gift_cents, pct: 100, tone: "ok", label: `${formatUsdCents(v.free.gift_cents)} to start` };
  if (!v.plan && v.free) return balanceGauge(v.credit.balance_cents, v.free.gift_cents);
  const left = Math.max(0, v.credit.balance_cents);
  return { used: left, allowed: 0, pct: left > 0 ? 100 : 0, tone: left > 0 ? "ok" : "over", label: `${formatUsdCents(left)} left` };
}

/** The line under the credit gauge for a plan that runs on top-ups. */
export function creditFoot(v: Pick<AccountView, "credit">): string {
  return v.credit.balance_cents > 0 ? "Top up any time. Credit never expires." : "No credit left — top up to keep building. Your apps keep running.";
}

/** What the free trial gives, or how far it is — one line. */
export function freeLine(f: FreeView, now: Date): string {
  if (f.state === "ended") return "Your free month is over. Subscribe to keep building — your work is saved.";
  if (f.state === "open" && !f.seats_open) return "The free places are full right now. Subscribe to get your own, or try again later.";
  if (f.state === "open") return `Free trial: 1 app, preview only, ${formatUsdCents(f.gift_cents)} of build credit to start. No card needed.`;
  const days = f.ends_at ? Math.ceil((Date.parse(f.ends_at) - now.getTime()) / 86400_000) : 0;
  const left = days <= 1 ? "last day" : `${days} days left`;
  return `Free trial · ${left} · today ${formatUsdCents(f.day_used_cents)} of ${formatUsdCents(f.day_limit_cents)} · preview only`;
}

/** `renews 1 Oct 2026` from the ISO instant the plan credits again. */
export function renewsLine(renewsAt: string): string {
  const d = new Date(renewsAt);
  return `renews ${d.getUTCDate()} ${d.toLocaleString("en-US", { month: "short", timeZone: "UTC" })} ${d.getUTCFullYear()}`;
}

/** Per-app rows, largest first; unattributed spend ("_") named as such, last; an unmeasured value stays null. */
export function appRows(byApp: Record<string, number | null>): Array<{ app: string; value: number | null }> {
  return Object.entries(byApp)
    .map(([app, value]) => ({ app: app === "_" ? "(not attributed to an app)" : app, value, last: app === "_" }))
    .sort((a, b) => Number(a.last) - Number(b.last) || (b.value ?? -1) - (a.value ?? -1))
    .map(({ app, value }) => ({ app, value }));
}

/** The page's headline: what the account has, in one line. */
export function planHeadline(v: AccountView): string {
  if (v.suspended_at) return "Suspended — your plan ended. Pick a plan to come back.";
  if (!v.plan && v.free?.state === "open") return "Free trial — start your first app.";
  if (!v.plan && v.free?.state === "running") return "Free trial";
  if (!v.plan && v.free?.state === "ended") return "Free month over — pick a plan to keep building.";
  if (!v.plan) return "No plan yet — pick one to start building.";
  if (v.plan.price_eur != null) return `${PLAN_NAME[v.plan.id]} · ${priceLine({ ...v.plan, price_eur_year: undefined })}`;
  return `${PLAN_NAME[v.plan.id]} · ${v.plan.price_usd} $ / month`;
}

/** The warning under the headline, or null: a grace running, or a suspension. */
export function planNotice(v: AccountView): { tone: "warn" | "over"; text: string } | null {
  if (v.suspended_at) return { tone: "over", text: `Suspended since ${v.suspended_at.slice(0, 10)}: builds are off and your sites are being taken down. A new plan brings everything back.` };
  if (v.plan_ends_at) return { tone: "warn", text: `Your plan ends on ${v.plan_ends_at.slice(0, 10)}. After that: no builds, sites unpublished, files unreachable. Renew to keep everything.` };
  return null;
}

// ── LE BOUTON D'UN PLAN ─────────────────────────────────────────────────────
//
// Cette décision était une cascade de quatre ternaires au milieu du JSX de la page Compte. Elle
// en sort pour deux raisons, et la seconde est la vraie : d'abord parce qu'une cascade ne se lit
// pas, ensuite parce que le premier écran d'un nouvel arrivant montre LES MÊMES cartes — et deux
// copies de cette cascade se seraient contredites au premier changement.

export type PlanButton = { label: string; disabled: boolean };

/**
 * Que dit le bouton de ce plan, et peut-on cliquer dessus ?
 *
 * `busy` est le plan dont la caisse est en train de s'ouvrir — un seul à la fois : deux caisses
 * ouvertes, c'est deux abonnements achetés.
 */
export function planButton(v: Pick<AccountView, "plan" | "plan_ends_at">, planId: Plan["id"], busy: string | null): PlanButton {
  if (busy === planId) return { label: "Opening checkout…", disabled: true };
  // UN AUTRE PLAN EN COURS D'ACHAT ferme tous les boutons, pas seulement le sien.
  if (busy !== null) return { label: labelFor(v, planId), disabled: true };
  const sien = v.plan?.id === planId;
  // SON PLAN ACTUEL, non résilié : rien à acheter. C'est le SEUL cas où le bouton est éteint sans
  // qu'une caisse soit ouverte — et l'oublier ferait racheter à quelqu'un ce qu'il a déjà.
  return { label: labelFor(v, planId), disabled: sien && !v.plan_ends_at };
}

function labelFor(v: Pick<AccountView, "plan" | "plan_ends_at">, planId: Plan["id"]): string {
  const sien = v.plan?.id === planId;
  if (sien && v.plan_ends_at) return "Renew";          // résilié, dans sa période de grâce
  if (sien) return "Your plan";
  if (v.plan) return `Switch to ${PLAN_NAME[planId]}`;
  return `Pick ${PLAN_NAME[planId]}`;                   // personne n'a encore de plan : le premier jour
}

/**
 * Quel plan porte le bouton plein ?
 *
 * ── UNE RÈGLE DE COMMERCE, PAS DE MISE EN PAGE ──────────────────────────────
 *
 * Trois boutons pleins côte à côte ne donnent aucun point d'entrée à l'œil. La solution courante
 * — mettre en avant le plan du MILIEU — est un coup de pouce vers le plus cher, et les grilles de
 * tarifs qui vendent ne font que ça.
 *
 * Ici c'est le MOINS CHER, et seulement quand personne n'a encore de plan. Un coup de pouce vers
 * l'entrée de gamme ne se reproche pas, et c'est aussi le conseil honnête : une app, on commence
 * par une. Quelqu'un qui a déjà un plan n'a rien à se faire pousser du tout — `null`, et tous les
 * boutons se valent.
 *
 * Cette règle vit ici, et pas dans le JSX, pour qu'un test puisse la tenir : « on ne met jamais en
 * avant le plan le plus cher » est une promesse, pas une classe CSS.
 */
export function entryPlan<P extends { id: Plan["id"]; price_usd?: number; price_eur?: number }>(
  plans: readonly P[],
  view: Pick<AccountView, "plan">,
): P | null {
  if (view.plan !== null) return null;
  const price = (p: P) => p.price_eur ?? p.price_usd ?? Infinity;
  return plans.reduce<P | null>((bas, p) => (bas === null || price(p) < price(bas) ? p : bas), null);
}
